import clsx from 'clsx';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';

// Toolbar button that opens the AI prompt menu. The menu itself lives
// inside PlateEditor (so it owns the imperative ref). Instead of drilling
// the ref through to every toolbar that wants to invoke it, we dispatch
// a custom `plate-ai-toggle` event on `document`; PlateEditor listens
// for it and calls `aiMenuRef.current?.toggle()` directly. Works
// regardless of where the toolbar is rendered (PlateToolbar portals to
// the host slot, FloatingToolbar portals to <body>).
//
// Render gate: this button reads the `editor.AI.enabled` visibility flag
// directly so it disappears from BOTH toolbars (PlateToolbar +
// FloatingToolbar) without each parent having to do its own check. The
// PlateEditor's own AIMenu listener is gated independently by the same
// flag (via DealSummaryEditorPlate threading it into the `enableAI` prop),
// so the click + the menu render stay in lockstep.
export const AI_TOGGLE_EVENT = 'plate-ai-toggle';

interface AIToolbarButtonProps {
    /**
     * Override the default PlateToolbar button class. FloatingToolbar passes
     * its own `styles.btn` so the button visually matches its host toolbar
     * instead of inheriting the main toolbar's chrome.
     */
    className?: string;
    /** Shown in the tooltip alongside the ⌘J hint. */
    label?: string;
}

export function AIToolbarButton({ className, label = 'AI assistant' }: AIToolbarButtonProps) {
    // Gate render on `editor.AI.enabled`. `.write` means level === 1 (fully
    // enabled). Missing key, level 0 (read), or level -1 (hidden) all return
    // false → button doesn't render. Mirrors the PlateEditor-side listener
    // gate so the surface is consistently absent when AI is off.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });
    if (!aiEnabled.write) return null;

    const handleMouseDown = (e: React.MouseEvent) => {
        // Same pattern as the other toolbar buttons — preventDefault keeps
        // the editor's selection alive so the AI menu can read the selected
        // text (used as context for the prompt).
        e.preventDefault();
        document.dispatchEvent(new CustomEvent(AI_TOGGLE_EVENT));
    };

    return (
        <button
            type="button"
            data-ai-trigger
            className={clsx(className ?? toolbarStyles.btn)}
            onMouseDown={handleMouseDown}
            title={`${label} (⌘J / Ctrl+J)`}
            aria-label={label}
            aria-keyshortcuts="Meta+J Control+J"
        >
            <AutoAwesomeIcon fontSize="small" />
        </button>
    );
}
